import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    Plus, 
    Search, 
    Edit2, 
    Trash2, 
    Eye, 
    Users, 
    CheckCircle, 
    XCircle,
    ChevronRight,
    Briefcase,
    MapPin,
    Building2
} from 'lucide-react';
import { useGetAdminJobsQuery, useDeleteJobMutation, useUpdateJobMutation } from '../../store/slices/apiSlice';

const AdminJobs = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const { data: jobsData, isLoading, refetch } = useGetAdminJobsQuery();
    const [deleteJob] = useDeleteJobMutation();
    const [updateJob] = useUpdateJobMutation();

    const jobs = jobsData?.data || [];
    const filteredJobs = jobs.filter(job => 
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this job opening?')) {
            await deleteJob(id).unwrap();
            refetch();
        }
    };

    const toggleStatus = async (job: any) => {
        await updateJob({ id: job.id, data: { isActive: !job.isActive } }).unwrap();
        refetch();
    };

    return (
        <div className="space-y-6">
      <h1 className="sr-only">Admin Jobs</h1>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input 
                            type="text" 
                            placeholder="Search jobs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <Link 
                            to="/admin/jobs/applications"
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-200"
                        >
                            <Users className="w-4 h-4" />
                            View All Applications
                        </Link>
                        <Link 
                            to="/admin/jobs/add"
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-lg shadow-blue-200"
                        >
                            <Plus className="w-4 h-4" />
                            Post New Job
                        </Link>
                    </div>
                </div>

                {/* Jobs Table/Cards */}
                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-600">Job Title</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-600">Department</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-600">Type</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-600">Location</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-600 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {filteredJobs.map((job) => (
                                        <tr key={job.id} className="hover:bg-blue-50/30 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-blue-600 p-2 rounded-lg text-white shadow-sm">
                                                        <Briefcase className="w-4 h-4" />
                                                    </div>
                                                    <span className="font-semibold text-gray-900">{job.title}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                <div className="flex items-center gap-1">
                                                    <Building2 className="w-4 h-4 text-gray-400" />
                                                    {job.department || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                                    {job.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                <div className="flex items-center gap-1">
                                                    <MapPin className="w-4 h-4 text-gray-400" />
                                                    {job.location}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <button 
                                                    onClick={() => toggleStatus(job)}
                                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                                                        job.isActive 
                                                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                                                    }`}
                                                >
                                                    {job.isActive ? (
                                                        <><CheckCircle className="w-3 h-3" /> Active</>
                                                    ) : (
                                                        <><XCircle className="w-3 h-3" /> Disabled</>
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Link 
                                                        to={`/careers/${job.id}`} 
                                                        target="_blank"
                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                                        title="Preview Publicly"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Link>
                                                    <Link 
                                                        to={`/admin/jobs/edit/${job.id}`}
                                                        className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                                                        title="Edit Job"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Link>
                                                    <button 
                                                        onClick={() => handleDelete(job.id)}
                                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                                        title="Delete Job"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                    <Link 
                                                        to={`/admin/jobs/applications/${job.id}`}
                                                        className="flex items-center gap-1 ml-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-xs font-bold shadow-md shadow-blue-100"
                                                    >
                                                        Applications
                                                        <ChevronRight className="w-3 h-3" />
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredJobs.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-10 text-center text-gray-500 italic">
                                                No job openings found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
        </div>
    );
};

export default AdminJobs;
